import ITask from './ITask';
import { OverlayFadeIn, OverlayFadeOut, Scale } from 'System/Animation/Clip';
import { AnimatorSystem, AwaitIterators, ConcatIterators, PlayAnimation } from 'System/Animation/AnimationHelper';
import { delay } from './TaskQueue';
import WorldSystem from 'System/World/WorldSystem';
import GameObject from 'GameObject/GameObject';
import WorldHUDHandler from 'System/UI/HUD/WorldHUDHandler';
import { RenderSystemEvent } from 'System/Render/RenderSystem';
import OverlayComponent from 'Component/UI/Overlay';
import Game from 'Game';
import Point from '../Point';
import Text from 'Prototype/Text';
import { Easing } from '@tweenjs/tween.js';
import { PlayerActions } from 'State/Player/PlayerActions';
import { PopupID } from 'State/UI/PopupID';
import { UIActions } from 'State/UI/UIActions';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { areEventAssetsLoaded } from 'State/Events/EventHelper';
import { IEventStateData } from 'State/Events/IEventsState';
import { report } from 'report';
import { CurrentEventFTUXString } from 'Constants/Events/CurrentEvent';

export default class EventFTUXTask implements ITask<WorldSystem> {
    public *execute(system: AnimatorSystem): IterableIterator<any> {
        const state = system.store.getState();
        const possibleEvents = EventStateHelper.getEvents(state, {
            isUnlocked: true,
            hasFTUX: true,
        }).filter(event => {
            return (
                // Ftux must not have been completed
                !FTUXStateHelper.isFtuxCompleted(state.playerState, event.type) &&
                // All assets must be loaded
                areEventAssetsLoaded(event)
            );
        });

        if (possibleEvents.length === 0) return;

        const event = ArrayHelper.sortByHighestPriority(possibleEvents)[0];

        UIActions.showPopup(PopupID.EVENT_FTUX, { eventType: event.type });

        yield* this.yieldUntilPopupClosed(PopupID.EVENT_FTUX);

        if (event.hasQuests) {
            yield* this.showEventQuestFTUX(system, event);
        }

        PlayerActions.setFTUXUnlocks([event.type]);
    }

    private *showEventQuestFTUX(system: AnimatorSystem, event: IEventStateData) {
        system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, system.animator);
        const overlay = system.animator.getChildByName('overlay');
        const questButton = GameObject.findFirstByName(WorldHUDHandler.QUESTS_BTN_NAME);
        if (!questButton) {
            report("EventFTUXTask Failed: Can't find quest button", {
                once: true,
                sampleRate: 0.1,
                tags: {
                    eventType: event.type,
                },
                fingerprint: 'showEventQuestFTUX:MissingQuestButton',
            });
        }
        const questButtonParent = questButton.parent;

        const text = GameObject.Instantiate(Text, {
            relativePosition: new Point(0.5, 0.77),
            text: Game.locale.getText(CurrentEventFTUXString),
            style: Game.style.getStyle('quests.FTUX'),
            anchor: Point.Center,
            scale: Point.Zero,
        });
        system.animator.addChild(text);
        system.animator.addChild(questButton);

        overlay.getComponent(OverlayComponent).preventInteraction = true;

        yield* ConcatIterators([
            PlayAnimation(new OverlayFadeIn(overlay)),
            PlayAnimation(
                new Scale(text, {
                    startScale: Point.Zero,
                    endScale: Point.One,
                    duration: 250,
                    easing: Easing.Back.Out,
                })
            ),
            this.yieldUntilPopupOpened(PopupID.QUEST_MENU),
            AwaitIterators([
                PlayAnimation(new OverlayFadeOut(overlay)),
                PlayAnimation(
                    new Scale(text, {
                        startScale: Point.One,
                        endScale: Point.Zero,
                        duration: 250,
                        easing: Easing.Back.In,
                    })
                ),
            ]),
            this.yieldUntilPopupClosed(PopupID.QUEST_MENU),
        ]);
        questButtonParent.addChild(questButton);
        overlay.getComponent(OverlayComponent).preventInteraction = false;
    }

    private *yieldUntilPopupClosed(popupID: PopupID) {
        let state = Game.store.getState();
        while (state.UIState.popupID === popupID) {
            yield* delay(100);
            state = Game.store.getState();
        }
    }

    private *yieldUntilPopupOpened(popupID: PopupID) {
        let state = Game.store.getState();
        while (state.UIState.popupID !== popupID) {
            yield* delay(100);
            state = Game.store.getState();
        }
    }
}
